import { faSortAmountDown, faSortAmountUpAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import { SortChangeHandler } from 'components/data_table/types';
import {
    DataSourceSort,
    DataSourceSortDirection,
} from 'custom_hooks/data_hooks/data_source/data_source';
import * as React from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const SortIconWrapper = styled.a`
    color: ${tokens.icon.actionActive};
`;

/**
 * Returns the next sorting info.
 *
 * @param fields The sort field name(s).
 * @param currentDirection The current sort direction.
 */
export const getNextSortState = (
    fields: string[],
    currentDirection: DataSourceSortDirection | undefined
): DataSourceSort[] => {
    if (fields.length === 0) {
        throw new Error('Missing sort fields');
    }
    switch (currentDirection) {
        case undefined:
            return fields.map((field) => ({ field, dir: 'asc' }));
        case 'asc':
            return fields.map((field) => ({ field, dir: 'desc' }));
        case 'desc':
            return [];
        default:
            throw new Error('Invalid sort direction');
    }
};

/**
 * Returns the aria label for the sort icon.
 */
export const getAriaLabel = (currentDirection: DataSourceSortDirection | undefined) => {
    switch (currentDirection) {
        case undefined:
            return 'ascending';
        case 'asc':
            return 'descending';
        case 'desc':
            return 'no sort';
        default:
            throw new Error('Invalid sort direction');
    }
};

export type SortIconProps = Readonly<{
    /**
     * The field(s) used for sorting.
     */
    fields: string[];
    /**
     * The sort direction (`undefined` for unsorted).
     */
    sortDirection: DataSourceSortDirection | undefined;
    /**
     * Callback for when sort icon is activated and the sort needs to change.
     */
    onChange: SortChangeHandler | undefined;
}>;

export function SortIcon({ fields, sortDirection, onChange }: SortIconProps) {
    const icon = sortDirection === 'desc' ? faSortAmountDown : faSortAmountUpAlt;

    const onClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.preventDefault();
        if (onChange) {
            onChange(getNextSortState(fields, sortDirection));
        }
    };

    return (
        <Button onClick={onClick} aria-label={`Sort ${getAriaLabel(sortDirection)}`}>
            <SortIconWrapper>
                <FontAwesomeIcon icon={icon} />
            </SortIconWrapper>
        </Button>
    );
}
